import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { KundeKortService } from 'src/app/services/kunde-kort.service';
import {
  GarantiomsetningService, GarantiSystem, GarantiPayerSource, GARANTI_STATUSES, CoffeeJuiceTurnover,
  garantiAfterProsent, sumAchievedIncVat, sumAchievedExVat, shortfallExVat, kickbackAmount, netToInvoice,
  garantiPeriodFor, garantiPeriodLabel
} from 'src/app/services/garantiomsetning.service';
import { MembershipV2Service } from 'src/app/services/membership-v2.service';
import { AuthService } from 'src/app/services/auth.service';

interface GarantiRow {
  systemId: string;
  systemName: string;
  companyId: string;
  kundekortId: string | null;
  customerName: string;
  payerSource: GarantiPayerSource;
  linkedKundekortIds: string[];
  garantiEksMva: number;       // read-only here — lives on the system (System card).
  kickbackRate: number | null; // read-only here — lives on the kundekort (Operations tab).
  prosentIDrift: number;
  vendorAchievedIncVat: number;
  friggAppAchievedIncVat: number;
  coffeeJuiceAchievedIncVat: number;
  coffeeJuiceDetail: string;
  periodMonths: number;        // invoiced every N months — lives on the system (system_info.garantiPeriodMonths)
  periodStart: string | null;
  periodNote: string;          // '' when monthly; otherwise what happens to this month's figures
  status: string;
  note: string;
  computing: boolean;
  saving: boolean;
}

// Monthly guaranteed-turnover ("garantiomsetning") shortfall billing.
// Replicates the manually-maintained Excel: for every SYSTEM with a
// guaranteed monthly turnover (garantiomsetning lives on the system, same
// field the System card and the sales-report income-vs-garanti graph already
// use — NOT on the kundekort), compare the prorated guarantee against actual
// achieved sales — the Frigg-app channel summed live from transactions, plus
// a manually-entered vendor/vending-machine channel Frigg's system has no
// visibility into — and surface the ex-VAT shortfall to invoice the linked
// customer, with any per-customer kickback commission as a minus line.
@Component({
  selector: 'garantiomsetning',
  templateUrl: './garantiomsetning.component.html',
  styleUrls: ['../../shared/styles/crm-billing.css', './garantiomsetning.component.css']
})
export class GarantiomsetningComponent implements OnInit, OnChanges, OnDestroy {

  // Driven by the parent Billing page's month picker (see billing.component.html:
  // <garantiomsetning [month]="invoiceMonth">) so both sections stay in sync —
  // this component has no month picker of its own.
  @Input() month: string = moment().format('YYYY-MM');
  private initialized = false;
  statuses = GARANTI_STATUSES;
  loading = false;
  loadError = '';
  saveError = '';
  recomputingAll = false;

  rows: GarantiRow[] = [];
  companiesMap: { [id: string]: string } = {};

  private systems: GarantiSystem[] = [];
  private customersById: { [id: string]: { name: string, kickbackRate: number | null } } = {};
  // Payer picker: one row edits at a time, so only one autocomplete is ever rendered.
  private customerList: { id: string, name: string }[] = [];
  editingPayerFor: string | null = null;
  payerQuery = '';
  payerOptions: { id: string, name: string, linked: boolean }[] = [];
  private rowsSub: Subscription;
  private adminEmail = '';
  private subs: Subscription[] = [];
  // Coffee + juice for the whole month in one call (Vendon is per machine, not
  // per row) — shared by every row's recompute, reset when the month changes.
  private coffeeJuice: { month: string, data: Promise<{ [systemId: string]: CoffeeJuiceTurnover }> } | null = null;

  constructor(
    private kundeKort: KundeKortService,
    private garanti: GarantiomsetningService,
    private membership: MembershipV2Service,
    private auth: AuthService
  ) { }

  ngOnInit() {
    this.subs.push(this.auth.user$.subscribe(u => this.adminEmail = u ? u.email : ''));
    this.membership.getCompaniesMapOnce().then(m => this.companiesMap = m).catch(() => { });
    this.subs.push(this.kundeKort.getCustomers().subscribe((c: any[]) => {
      this.customersById = {};
      (c || []).forEach(x => this.customersById[x.kundekortId] = { name: x.name || '(unnamed)', kickbackRate: x.kickbackRate != null ? Number(x.kickbackRate) : null });
      this.customerList = Object.keys(this.customersById)
        .map(id => ({ id, name: this.customersById[id].name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'nb'));
      this.initialized = true;
      this.load();
    }));
  }

  ngOnChanges(changes: SimpleChanges) {
    // The parent's month picker changed — reload with the new month. Ignore
    // the initial binding (handled once ngOnInit's own data pipeline fires).
    if (changes['month'] && !changes['month'].firstChange && this.initialized) {
      this.load();
    }
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
    if (this.rowsSub) { this.rowsSub.unsubscribe(); }
  }

  companyName(id: string) { return this.companiesMap[id] || (id ? id : '—'); }

  get groupedRows(): { companyId: string, rows: GarantiRow[] }[] {
    const byCompany: { [id: string]: GarantiRow[] } = {};
    this.rows.forEach(r => (byCompany[r.companyId || ''] = byCompany[r.companyId || ''] || []).push(r));
    return Object.keys(byCompany)
      .sort((a, b) => this.companyName(a).localeCompare(this.companyName(b)))
      .map(companyId => ({ companyId, rows: byCompany[companyId].sort((a, b) => a.systemName.localeCompare(b.systemName)) }));
  }

  // groupedRows below returns fresh wrapper objects every change-detection
  // cycle — without trackBy, *ngFor would treat that as "all new" on every
  // keystroke/async tick and tear down + rebuild every row's DOM (dropping
  // focus mid-edit). Track by the stable id instead of object identity.
  trackGroup(_i: number, g: { companyId: string }) { return g.companyId; }
  trackRow(_i: number, r: GarantiRow) { return r.systemId; }

  // ---- derived (view) figures — delegate to the shared pure functions so
  // this table and the Billing page's invoice builder never drift apart. ---
  garantiAfterProsent(r: GarantiRow) { return garantiAfterProsent(r); }
  sumAchievedIncVat(r: GarantiRow) { return sumAchievedIncVat(r); }
  sumAchievedExVat(r: GarantiRow) { return sumAchievedExVat(r); }
  shortfallExVat(r: GarantiRow) { return shortfallExVat(r); }
  kickbackAmount(r: GarantiRow) { return kickbackAmount(r); }
  netToInvoice(r: GarantiRow) { return netToInvoice(r); }

  // Rows billed every N months are not invoiced from this month's figures alone.
  get totalBillable(): number { return this.rows.reduce((s, r) => s + (r.periodNote ? 0 : this.netToInvoice(r)), 0); }
  get totalGaranti(): number { return this.rows.reduce((s, r) => s + this.garantiAfterProsent(r), 0); }
  get totalAchievedExVat(): number { return this.rows.reduce((s, r) => s + this.sumAchievedExVat(r), 0); }
  get unlinkedCount(): number { return this.rows.filter(r => !r.kundekortId).length; }

  // ---- who pays the shortfall ----------------------------------------------
  payerHint(r: GarantiRow): string {
    switch (r.payerSource) {
      case 'set': return 'Chosen here';
      case 'linked': return 'The only customer linked to this system (Kundekort → Systems). Choose one here to make it explicit.';
      case 'ambiguous': return `Linked to ${r.linkedKundekortIds.length} customers: ${r.linkedKundekortIds.map(id => this.custName(id)).join(', ')}. Choose who pays.`;
      default: return 'Not linked to any customer. Choose who pays.';
    }
  }

  private custName(id: string) { return this.customersById[id] ? this.customersById[id].name : id; }

  startEditPayer(r: GarantiRow) {
    this.editingPayerFor = r.systemId;
    this.payerQuery = '';
    this.filterPayers(r, '');
  }

  cancelEditPayer() { this.editingPayerFor = null; }

  // Customers linked to the system first, then everyone else matching the search.
  filterPayers(r: GarantiRow, q: string) {
    const needle = String(q || '').trim().toLowerCase();
    const match = (c: { name: string }) => !needle || c.name.toLowerCase().includes(needle);
    const linked = r.linkedKundekortIds.filter(id => this.customersById[id])
      .map(id => ({ id, name: this.custName(id), linked: true })).filter(match);
    const others = this.customerList.filter(c => !r.linkedKundekortIds.includes(c.id) && match(c))
      .slice(0, 50).map(c => ({ ...c, linked: false }));
    this.payerOptions = [...linked, ...others];
  }

  async choosePayer(r: GarantiRow, kundekortId: string | null) {
    this.editingPayerFor = null;
    r.saving = true; this.saveError = '';
    try {
      await this.garanti.setGarantiPayer(r.systemId, kundekortId, this.adminEmail);
      // Re-resolve from Firestore so a cleared payer falls back exactly as billing will.
      this.systems = await this.garanti.getGarantiSystems();
      const sys = this.systems.find(x => x.systemId === r.systemId);
      if (sys) { this.applyPayer(r, sys); }
      await this.persist(r);
    } catch (err) {
      this.saveError = `Could not save who pays for ${r.systemName} (permissions or network error).`;
    } finally {
      r.saving = false;
    }
  }

  private applyPayer(r: GarantiRow, sys: GarantiSystem) {
    const cust = sys.kundekortId ? this.customersById[sys.kundekortId] : null;
    r.kundekortId = sys.kundekortId;
    r.payerSource = sys.payerSource;
    r.linkedKundekortIds = sys.linkedKundekortIds;
    r.customerName = cust ? cust.name
      : sys.payerSource === 'ambiguous' ? `— ${sys.linkedKundekortIds.length} linked customers —` : '— no customer —';
    r.kickbackRate = cust ? cust.kickbackRate : null;
  }

  // ---- how often the shortfall is invoiced ---------------------------------
  editingPeriodFor: string | null = null;
  periodDraft = { months: 1, start: '' };

  private applyPeriod(r: GarantiRow, sys: GarantiSystem) {
    r.periodMonths = sys.periodMonths;
    r.periodStart = sys.periodStart;
    const p = garantiPeriodFor(this.month, sys.periodMonths, sys.periodStart);
    r.periodNote = !p ? ''
      : p.isEnd ? `Invoiced now for ${garantiPeriodLabel(p.months)}, all systems with the same payer pooled — the amount is on the invoice preview`
      : `Part of ${garantiPeriodLabel(p.months)} — invoiced in ${p.months[p.months.length - 1]}`;
  }

  startEditPeriod(r: GarantiRow) {
    this.editingPeriodFor = r.systemId;
    this.periodDraft = { months: r.periodMonths || 1, start: r.periodStart || this.month };
  }

  cancelEditPeriod() { this.editingPeriodFor = null; }

  async savePeriod(r: GarantiRow) {
    this.editingPeriodFor = null;
    r.saving = true; this.saveError = '';
    try {
      await this.garanti.setGarantiPeriod(r.systemId, this.periodDraft.months, this.periodDraft.start, this.adminEmail);
      this.systems = await this.garanti.getGarantiSystems();
      const sys = this.systems.find(x => x.systemId === r.systemId);
      if (sys) { this.applyPeriod(r, sys); }
    } catch (err) {
      this.saveError = `Could not save the invoicing period for ${r.systemName} (permissions or network error).`;
    } finally {
      r.saving = false;
    }
  }

  // ---- load ---------------------------------------------------------------
  // Loads FAST: just the saved/cached figures, no per-row Firestore aggregate
  // queries. Frigg-app turnover is only ever computed on demand — via the
  // per-row refresh icon, the "Recompute all" button below, or automatically
  // once when Billing's "Run reconciliation" is clicked (see billing.component
  // -> recomputeAll()) — never reactively inside this subscription. Doing it
  // reactively used to retrigger itself: every recompute's own write echoed
  // back through this same live listener, which rebuilt every row from
  // scratch (losing track of which systems were already mid-request) and
  // fired recompute() again for all of them — a cascade of duplicate
  // Firestore calls that could make the page spin for a long time.
  async load() {
    this.loading = true; this.loadError = '';
    try {
      if (!this.systems.length) {
        this.systems = await this.garanti.getGarantiSystems();
      }
    } catch (err) {
      this.loading = false;
      this.loadError = 'Could not load systems (permissions or network error).';
      return;
    }
    if (!this.systems.length) { this.rows = []; this.loading = false; return; }

    if (this.rowsSub) { this.rowsSub.unsubscribe(); }
    this.rowsSub = this.garanti.getMonthRows$(this.month).subscribe({
      next: saved => {
        const bySystem: { [id: string]: any } = {};
        saved.forEach(s => bySystem[s.systemId] = s);

        this.rows = this.systems.map(sys => {
          const s = bySystem[sys.systemId] || {};
          const row = {
            systemId: sys.systemId,
            systemName: sys.name,
            companyId: sys.companyId,
            garantiEksMva: sys.garantiomsetning,
            prosentIDrift: s.prosentIDrift != null ? s.prosentIDrift : 1,
            vendorAchievedIncVat: s.vendorAchievedIncVat != null ? s.vendorAchievedIncVat : 0,
            friggAppAchievedIncVat: s.friggAppAchievedIncVat != null ? s.friggAppAchievedIncVat : 0,
            coffeeJuiceAchievedIncVat: s.coffeeJuiceAchievedIncVat != null ? s.coffeeJuiceAchievedIncVat : 0,
            coffeeJuiceDetail: s.coffeeJuiceDetail || '',
            status: s.status || '',
            note: s.note || '',
            computing: false,
            saving: false,
          } as GarantiRow;
          this.applyPayer(row, sys);
          this.applyPeriod(row, sys);
          return row;
        });
        this.loading = false;
      },
      error: () => {
        // Without this handler the spinner spins forever on any failure
        // (permission error, offline, etc.) — RxJS never calls `next` again
        // once an Observable errors.
        this.loading = false;
        this.loadError = 'Could not load garantiomsetning (permissions or network error).';
      }
    });
  }

  // ---- per-row actions ------------------------------------------------------
  async recompute(r: GarantiRow) {
    if (r.computing) { return; }   // already in flight for this row
    r.computing = true; this.saveError = '';
    const from = moment(this.month, 'YYYY-MM').startOf('month').unix();
    const to = moment(this.month, 'YYYY-MM').endOf('month').unix();
    try {
      if (!this.coffeeJuice || this.coffeeJuice.month !== this.month) {
        const data = this.garanti.computeCoffeeJuiceIncVat(this.month, this.systems);
        this.coffeeJuice = { month: this.month, data };
        data.catch(() => { if (this.coffeeJuice && this.coffeeJuice.data === data) { this.coffeeJuice = null; } });
      }
      const [app, coffeeJuice] = await Promise.all([
        this.garanti.computeAppTurnoverIncVat(r.systemId, from, to),
        this.coffeeJuice.data
      ]);
      r.friggAppAchievedIncVat = app;
      const cj = coffeeJuice[r.systemId];
      r.coffeeJuiceAchievedIncVat = cj ? cj.incVat : 0;
      r.coffeeJuiceDetail = cj ? cj.detail : '';
      await this.persist(r);
    } catch (err: any) {
      this.saveError = `Could not recompute ${r.systemName}: ${(err && err.message) || 'permissions or network error'}.`;
    } finally {
      r.computing = false;
    }
  }

  // Recomputes Frigg-app turnover for every row missing it (or every row, if
  // forced) — the explicit, on-demand alternative to the old reactive loop.
  // Called from the "Recompute all" button here, and once automatically from
  // Billing's "Run reconciliation" (see billing.component.runReconcile()).
  async recomputeAll(onlyMissing = false) {
    if (this.recomputingAll) { return; }
    const targets = onlyMissing ? this.rows.filter(r => !r.friggAppAchievedIncVat || !r.coffeeJuiceDetail) : this.rows;
    if (!targets.length) { return; }
    this.recomputingAll = true; this.saveError = '';
    try {
      await Promise.all(targets.map(r => this.recompute(r)));
    } finally {
      this.recomputingAll = false;
    }
  }

  onFieldChange(r: GarantiRow) {
    this.persist(r).catch(() => { this.saveError = `Could not save ${r.systemName} (permissions or network error).`; });
  }

  private persist(r: GarantiRow): Promise<void> {
    r.saving = true;
    return this.garanti.saveRow(r.systemId, this.month, r.kundekortId, {
      prosentIDrift: Number(r.prosentIDrift) || 0,
      vendorAchievedIncVat: Number(r.vendorAchievedIncVat) || 0,
      friggAppAchievedIncVat: Number(r.friggAppAchievedIncVat) || 0,
      coffeeJuiceAchievedIncVat: Number(r.coffeeJuiceAchievedIncVat) || 0,
      coffeeJuiceDetail: r.coffeeJuiceDetail || '',
      status: r.status || '',
      note: r.note || '',
    }, this.adminEmail).finally(() => { r.saving = false; });
  }

  // ---- export ---------------------------------------------------------------
  async exportXlsx() {
    if (!this.rows.length) { return; }
    const XLSX = await import('xlsx');
    const rows: any[][] = [[
      'System', 'Betales av', 'Garanti eks mva', 'Prosent i drift', 'Garanti etter prosentvis drift',
      'Nådd beløp i vendor ink mva', 'Nådd beløp i Frigg app ink mva', 'Kaffe og juice ink mva', 'Sum nådd beløp ink mva', 'Sum nådd eks mva',
      'Til fakturering eks mva', 'Kickback', 'Faktureres til kunde', 'Status', 'Notat'
    ]];
    this.groupedRows.forEach(g => {
      rows.push([this.companyName(g.companyId)]);
      g.rows.forEach(r => rows.push([
        r.systemName, r.customerName, r.garantiEksMva, r.prosentIDrift, this.garantiAfterProsent(r),
        r.vendorAchievedIncVat, r.friggAppAchievedIncVat, r.coffeeJuiceAchievedIncVat, this.sumAchievedIncVat(r), +this.sumAchievedExVat(r).toFixed(2),
        +this.shortfallExVat(r).toFixed(2), +this.kickbackAmount(r).toFixed(2), r.periodNote ? r.periodNote : +this.netToInvoice(r).toFixed(2), r.status, r.note
      ]));
    });
    rows.push(['Total', '', '', '', +this.totalGaranti.toFixed(2), '', '', '', '', +this.totalAchievedExVat.toFixed(2), '', '', +this.totalBillable.toFixed(2), '', '']);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Garantiomsetning');
    XLSX.writeFile(wb, `garantiomsetning-${this.month}.xlsx`);
  }
}
